import { NgModule, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule } from '@angular/router';

import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { paramsOrQueryParamsChange, use } from '@frontend/sports/common/core/utils/router';
import { WidgetResolver, WidgetSchemaMarkupResolver } from '@frontend/sports/widget/core/feature';
import { WidgetLayoutComponent } from '@frontend/sports/widget/feature/layout';

import { CalendarRedirectGuard } from '../calendar/calendar-redirect.guard';
import { CalendarResolver } from '../calendar/calendar.resolver';
import { EmptyComponent } from '../common/empty.component';
import { CompetitionPageResolver } from '../competition-list/competition-page.resolver';
import { SportRoute } from '../router/multi-route.model';
import {
    multiNameIdentifierExactMatcher,
    multiNameIdentifierMatcher,
    multiNameIdentifierMatcher_WithChildren,
    multiNameIdentifierMatcher_WithRegexMatcher,
    nameIdentifierMatcher,
} from '../router/name-identifier.matcher';
import { NoMatchRouteGuard } from '../router/no-match-route.guard';
import { regexMatcher } from '../router/regex.matcher';
import { SeoRedirectGuard } from '../seo/seo-redirect.guard';
import { SitemapRoutingToken } from '../sitemap/sitemap-routing';
import { WorldCupHubRouteGuard } from '../world-cup-hub/world-cup-hub-route.guard';
import { BetbuilderBreadcrumbsResolver, CouponBreadcrumbsResolver } from './coupons/coupon-breadcrumbs.resolver';
import { CouponListBreadcrumbsResolver } from './coupons/coupon-list-breadcrumbs.resolver';
import { CouponListResolver, CouponResolver } from './coupons/coupon.resolver';
import { canMatchDynamicCategoriesGuard } from './dynamic-categories/dynamic-categories-redirect.guard';
import { dynamicOfferCategoriesResolver } from './dynamic-categories/dynamic-categories.resolver';
import { marketGroupResolver } from './market-group/market-group.resolver';
import { CompetitionListResolver, CompetitionResolver } from './sport/competitions/competition.resolver';
import { EventListBreadcrumbsResolver } from './sport/event-list-breadcrumbs.resolver';
import { EventListSchemaMarkupResolver } from './sport/event-list-schema-markup.resolver';
import { SportBreadcrumbsResolver } from './sport/sport-breadcrumbs.resolver';
import { TournamentBreadcrumbsResolver } from './tournament/tournament-breadcrumbs.resolver';
import { TournamentLeagueBreadcrumbsResolver } from './tournament/tournament-league-breadcrumbs.resolver';
import { TournamentEventsResolver, TournamentResolver } from './tournament/tournament.resolver';

const routes: SportRoute[] = [
    {
        path: '',
        providers: [EventListBreadcrumbsResolver, EventListSchemaMarkupResolver, SportBreadcrumbsResolver],
        children: [
            {
                path: '',
                component: WidgetLayoutComponent,
                resolve: use(WidgetResolver),
                data: {
                    tag: RouteTag.SportLobby,
                    breadcrumbs: SportBreadcrumbsResolver,
                    schemaMarkup: WidgetSchemaMarkupResolver,
                },
            },
            {
                path: '{coupons}',
                providers: [
                    BetbuilderBreadcrumbsResolver,
                    CouponBreadcrumbsResolver,
                    CouponListBreadcrumbsResolver,
                    CouponListResolver,
                    CouponResolver,
                ],
                children: [
                    {
                        path: '',
                        loadComponent: () => import('../coupons/coupon-list.component').then((t) => t.CouponListComponent),
                        data: {
                            tag: RouteTag.Coupons,
                            breadcrumbs: CouponListBreadcrumbsResolver,
                            shouldKeepBreadcrumbs: true,
                        },
                        resolve: use(CouponListResolver),
                    },
                    {
                        path: '{betBuilder}',
                        loadComponent: () =>
                            import('../coupons/bet-builder/bet-builder-event-list.component').then((t) => t.BetBuilderEventListComponent),
                        data: {
                            subNavigationHiddenOnSmallScreen: true,
                            tag: RouteTag.CouponDetails,
                            breadcrumbs: BetbuilderBreadcrumbsResolver,
                            sitemap: SitemapRoutingToken.CouponSitemapResolver,
                        },
                        resolve: use(CouponResolver),
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        loadComponent: () => import('../coupons/coupon.component').then((t) => t.CouponComponent),
                        resolve: use(CouponResolver),
                        data: {
                            matcherParam: 'coupon',
                            subNavigationHiddenOnSmallScreen: true,
                            tag: RouteTag.CouponDetails,
                            breadcrumbs: CouponBreadcrumbsResolver,
                            sitemap: SitemapRoutingToken.CouponSitemapResolver,
                        },
                    },
                    {
                        matcher: multiNameIdentifierMatcher_WithChildren,
                        data: {
                            matcherParams: ['region', 'league'],
                            sitemap: SitemapRoutingToken.CouponSitemapResolver,
                        },
                        children: [
                            {
                                path: '{betBuilder}',
                                loadComponent: () =>
                                    import('../coupons/bet-builder/bet-builder-event-list.component').then((t) => t.BetBuilderEventListComponent),
                                resolve: use(CouponResolver),
                                data: {
                                    subNavigationHiddenOnSmallScreen: true,
                                    tag: RouteTag.CouponDetails,
                                    breadcrumbs: BetbuilderBreadcrumbsResolver,
                                },
                            },
                            {
                                matcher: nameIdentifierMatcher,
                                loadComponent: () => import('../coupons/coupon.component').then((t) => t.CouponComponent),
                                resolve: use(CouponResolver),
                                data: {
                                    matcherParam: 'coupon',
                                    subNavigationHiddenOnSmallScreen: true,
                                    tag: RouteTag.CouponDetails,
                                    breadcrumbs: CouponBreadcrumbsResolver,
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: '{calendar}',
                loadComponent: () =>
                    import(/* webpackChunkName: "ms-sport-calendar" */ '../calendar/calendar.component').then((m) => m.CalendarComponent),
                resolve: use(CalendarResolver),
                canActivate: [CalendarRedirectGuard],
                data: {
                    breadcrumbs: EventListBreadcrumbsResolver,
                    preResolve: (route: ActivatedRouteSnapshot) => {
                        inject(CalendarResolver).preResolve(route);
                    },
                },
            },
            {
                path: '{teamPages}',
                loadChildren: () => import(/* webpackChunkName: "ms-teampages" */ '../teampages/team-pages.module').then((m) => m.TeamPagesModule),
            },
            {
                path: '{conferences}',
                data: {
                    subNavigationHiddenOnSmallScreen: false,
                    tag: RouteTag.Competition,
                },
                children: [
                    {
                        path: '',
                        component: EmptyComponent,
                    },
                    {
                        matcher: multiNameIdentifierExactMatcher,
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        data: {
                            matcherParams: ['league', 'group', 'conference'],
                            schemaMarkup: EventListSchemaMarkupResolver,
                            breadcrumbs: EventListBreadcrumbsResolver,
                            sitemap: SitemapRoutingToken.CompetitionSitemapResolver,
                            preResolve: (route: ActivatedRouteSnapshot) => {
                                inject(CompetitionResolver).preResolve(route);
                            },
                        },
                        loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                        resolve: use(CompetitionResolver),
                    },
                ],
            },
            {
                matcher: regexMatcher,
                data: {
                    matcherPattern: '^({betting}|{in30minutes}|{in60minutes}|{in180minutes}|{today}|{tomorrow}|{after2days}|{after3days})$',
                    matcherMapping: {
                        context: 1,
                    },
                    subNavigationHiddenOnSmallScreen: false,
                    tag: RouteTag.Competition,
                    breadcrumbs: EventListBreadcrumbsResolver,
                    sitemap: SitemapRoutingToken.SitemapEmptyResolver,
                },
                children: [
                    {
                        path: '{competitions}',
                        loadComponent: () => import('../competition-list/competition-list.component').then((t) => t.CompetitionListComponent),
                        resolve: use(CompetitionListResolver),
                        data: {
                            tag: RouteTag.Competition,
                            breadcrumbs: EventListBreadcrumbsResolver,
                            subNavigationHiddenOnSmallScreen: false,
                            shouldKeepBreadcrumbs: true,
                            preResolve: (route: ActivatedRouteSnapshot) => {
                                inject(CompetitionListResolver).preResolve(route);
                            },
                        },
                    },
                    {
                        path: '',
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                        resolve: use(CompetitionResolver),
                        data: {
                            sitemap: SitemapRoutingToken.SportSitemapResolver,
                            shouldKeepBreadcrumbs: true,
                            preResolve: (route: ActivatedRouteSnapshot) => {
                                inject(CompetitionResolver).preResolve(route);
                            },
                        },
                    },
                    {
                        matcher: regexMatcher,
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        data: {
                            matcherPattern: String.raw`(\d+(,\d+)+)`,
                            matcherMapping: {
                                league: 1,
                            },
                            sitemap: SitemapRoutingToken.CompetitionSitemapResolver,
                            preResolve: (route: ActivatedRouteSnapshot) => {
                                inject(CompetitionResolver).preResolve(route);
                            },
                        },
                        loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                        resolve: use(CompetitionResolver),
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        data: {
                            matcherParam: 'region',
                            sitemap: SitemapRoutingToken.RegionSitemapResolver,
                        },
                        children: [
                            {
                                path: '',
                                runGuardsAndResolvers: paramsOrQueryParamsChange,
                                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                                resolve: use(CompetitionResolver),
                                data: {
                                    shouldKeepBreadcrumbs: true,
                                    preResolve: (route: ActivatedRouteSnapshot) => {
                                        inject(CompetitionResolver).preResolve(route);
                                    },
                                },
                            },
                            {
                                matcher: multiNameIdentifierMatcher_WithRegexMatcher,
                                runGuardsAndResolvers: paramsOrQueryParamsChange,
                                data: {
                                    matcherParams: ['league', 'group'],
                                    matcherPattern: '{betting}',
                                    matcherMode: 'full',
                                    tag: RouteTag.Competition,
                                    schemaMarkup: WidgetSchemaMarkupResolver,
                                    sitemap: SitemapRoutingToken.CompetitionSitemapResolver,
                                },
                                component: WidgetLayoutComponent,
                                resolve: use(CompetitionPageResolver),
                            },
                            {
                                matcher: multiNameIdentifierMatcher,
                                runGuardsAndResolvers: paramsOrQueryParamsChange,
                                data: {
                                    preResolve: (route: ActivatedRouteSnapshot) => {
                                        inject(CompetitionResolver).preResolve(route);
                                    },
                                    matcherParams: ['league', 'group'],
                                    schemaMarkup: EventListSchemaMarkupResolver,
                                    sitemap: SitemapRoutingToken.CompetitionSitemapResolver,
                                },
                                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                                resolve: use(CompetitionResolver),
                            },
                        ],
                    },
                    {
                        path: '**',
                        component: EmptyComponent,
                        canActivate: [NoMatchRouteGuard, SeoRedirectGuard],
                    },
                ],
            },
            {
                path: '{tournaments}',
                providers: [TournamentBreadcrumbsResolver],
                children: [
                    {
                        path: ':tournament',
                        data: {
                            tag: RouteTag.Competition,
                            subNavigationHiddenOnSmallScreen: true,
                        },
                        children: [
                            {
                                path: '',
                                loadComponent: () => import('../tournament/tournament.component').then((t) => t.TournamentComponent),
                                resolve: use(TournamentResolver),
                                data: {
                                    breadcrumbs: TournamentBreadcrumbsResolver,
                                    preResolve: (route: ActivatedRouteSnapshot) => {
                                        inject(TournamentResolver).preResolve(route);
                                    },
                                },
                            },
                            {
                                matcher: nameIdentifierMatcher,
                                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                                resolve: use(TournamentEventsResolver),
                                data: {
                                    matcherParam: 'league',
                                    breadcrumbs: TournamentLeagueBreadcrumbsResolver,
                                    schemaMarkup: EventListSchemaMarkupResolver,
                                    preResolve: (route: ActivatedRouteSnapshot) => {
                                        inject(TournamentEventsResolver).preResolve(route);
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: '{worldCupHub}',
                data: {
                    tag: RouteTag.WorldCupHub,
                },
                loadComponent: () => import('../world-cup-hub/world-cup-hub.component').then((m) => m.WorldCupHubComponent),
                canActivate: [WorldCupHubRouteGuard],
            },
            {
                path: '**',
                component: EmptyComponent,
                canActivate: [NoMatchRouteGuard, SeoRedirectGuard],
            },
        ],
    },
];

const sportsBetsRoute: SportRoute[] = [
    {
        path: '',
        children: [
            {
                path: '',
                component: EmptyComponent,
            },
            {
                canMatch: [canMatchDynamicCategoriesGuard],
                matcher: nameIdentifierMatcher,
                runGuardsAndResolvers: paramsOrQueryParamsChange,
                data: {
                    matcherParam: 'sport',
                },
                children: [
                    {
                        path: '',
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        data: {
                            sitemap: SitemapRoutingToken.SportSitemapResolver,
                            tag: RouteTag.SportLobby,
                        },
                        loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                        resolve: { model: dynamicOfferCategoriesResolver },
                    },
                    {
                        matcher: nameIdentifierMatcher,
                        runGuardsAndResolvers: paramsOrQueryParamsChange,
                        data: {
                            matcherParam: 'region',
                            tag: RouteTag.Competition,
                        },
                        children: [
                            {
                                path: '',
                                runGuardsAndResolvers: paramsOrQueryParamsChange,
                                data: {
                                    sitemap: SitemapRoutingToken.RegionSitemapResolver,
                                },
                                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                                resolve: { model: dynamicOfferCategoriesResolver },
                            },
                            {
                                matcher: multiNameIdentifierMatcher,
                                runGuardsAndResolvers: paramsOrQueryParamsChange,
                                data: {
                                    matcherParams: ['league', 'conference'],
                                    sitemap: SitemapRoutingToken.CompetitionSitemapResolver,
                                },
                                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                                resolve: { model: dynamicOfferCategoriesResolver },
                            },
                        ],
                    },
                ],
            },
            {
                matcher: multiNameIdentifierMatcher,
                runGuardsAndResolvers: paramsOrQueryParamsChange,
                data: {
                    matcherParams: ['sport', 'region', 'league'],
                    sitemap: SitemapRoutingToken.MarketGroupSitemapResolver,
                },
                loadComponent: () => import('../fixture-list/fixture-list.component').then((t) => t.FixtureListComponent),
                resolve: { model: marketGroupResolver },
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class EventListRoutingModule {}

@NgModule({
    imports: [RouterModule.forChild(sportsBetsRoute)],
    exports: [RouterModule],
})
export class FixtureListRoutingModule {}
